import { Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { OrderStepBaseComponent } from 'app/order/order-step-base-component';
import {
    ProductAdviceComponent,
    QuestionItem,
} from '../../../../order/components/product-advice/product-advice.component';
import { OrderState } from 'app/order/models';
import { hasOrderStateTag } from 'app/order/fns';
import { ProductAdviceAnswers, ResultDecision } from '@models/product-advice';
import { ProductAdviceResultBoxComponent } from 'app/order/components/product-advice-result-box/product-advice-result-box.component';
import { OrderService } from 'app/order/state/order.service';
import { ProductsService } from 'app/products/products.service';
import { InsuranceTypesService, InsurenetInterfaceService } from '@core';
import { filter, switchMap } from 'rxjs';

const phoneQuestionItems: QuestionItem[] = [
    {
        controlName: 'HANDY_Q1',
        question: 'Was möchtest du vor allem absichern?',
        answerOptions: [
            { label: 'Schäden am Gerät', value: 'DAMAGE_ONLY' },
            { label: 'Schäden sowie Diebstahl / Raub', value: 'DAMAGE_AND_THEFT' },
        ],
    },
    {
        controlName: 'HANDY_Q2',
        question: 'Wie wichtig ist dir Schutz bei Diebstahl oder Raub?',
        answerOptions: [
            { label: 'Sehr wichtig', value: 'HIGH' },
            { label: 'Wichtig', value: 'MEDIUM' },
            { label: 'Weniger wichtig', value: 'LOW' },
            { label: 'Nicht wichtig', value: 'NONE' },
        ],
    },
    {
        controlName: 'HANDY_Q3',
        question:
            'Sind Sie bereit, im Schadenfall einen festen Eigenanteil zu tragen, um einen niedrigeren Beitrag zu erhalten?',
        answerOptions: [
            { label: 'Ja', value: 'ACCEPT' },
            { label: 'Nein', value: 'AVOID' },
        ],
    },
    {
        controlName: 'HANDY_Q4',
        question: 'Ist Ihnen wichtig, dass Ihr Handy auch auf Reisen beziehungsweise im Ausland geschützt ist?',
        answerOptions: [
            { label: 'Ja', value: 'IMPORTANT' },
            { label: 'Nein', value: 'NOT_IMPORTANT' },
        ],
    },
];

@Component({
    selector: 'app-product-advice-handy',
    templateUrl: './product-advice-handy.component.html',
    imports: [ProductAdviceComponent, ProductAdviceResultBoxComponent],
})
export class ProductAdviceHandyComponent extends OrderStepBaseComponent {
    private readonly orderService = inject(OrderService);
    private readonly productsService = inject(ProductsService);
    private readonly insurenetInterfaceService = inject(InsurenetInterfaceService);
    private readonly insuranceTypesService = inject(InsuranceTypesService);

    private readonly adviceComponent = viewChild(ProductAdviceComponent);

    protected hasDeductible = hasOrderStateTag('hasDeductible');
    protected hasTheftCoverage = hasOrderStateTag('hasTheftCoverage');
    protected questions = phoneQuestionItems;

    private initialInsuranceTypeId = signal<number | null>(null);
    protected recommendedInsuranceTypeId = signal<number | null>(null);
    protected recommendedPrice = signal<number | null>(null);
    private resultDecision: ResultDecision | null;

    protected formAnswers = signal<ProductAdviceAnswers>({});
    protected showAdvice = signal<boolean>(false);

    protected suggestTheftCoverageChange = computed<boolean>(() => {
        const answers = this.formAnswers();
        if (answers.HANDY_Q1 === 'DAMAGE_AND_THEFT' || answers.HANDY_Q2 === 'HIGH' || answers.HANDY_Q2 === 'MEDIUM') {
            return !this.hasTheftCoverage(); // should change when without theft coverage
        } else {
            return this.hasTheftCoverage(); // should change when with theft coverage
        }
    });

    protected suggestDeductibleChange = computed<boolean>(() => {
        const answers = this.formAnswers();
        if (answers.HANDY_Q3 === 'ACCEPT') {
            return !this.hasDeductible(); // should change when without deductible
        } else {
            return this.hasDeductible(); // should change when with deductible
        }
    });

    protected valid = computed(() => {
        const advice = this.state.advice;
        const productInsuranceTypeId = this.insuranceType()?.id;
        const recommendedInsuranceTypeId = this.recommendedInsuranceTypeId();

        if (advice?.mode === 'WAIVER' || productInsuranceTypeId == null || recommendedInsuranceTypeId == null) {
            return true;
        }

        return (
            (this.resultDecision === 'KEPT_ORIGINAL' && this.initialInsuranceTypeId() === productInsuranceTypeId) ||
            productInsuranceTypeId === recommendedInsuranceTypeId ||
            this.sameCoverageTags(this.state as OrderState, recommendedInsuranceTypeId)
        );
    });

    public constructor() {
        super();

        // update recommended insurance if selected
        // insurance changes but deductible and
        // theft coverage stay untouched
        effect(() => {
            const advice = this.state.advice;
            const productInsuranceTypeId = this.insuranceType()?.id;
            const recommendedInsuranceTypeId = this.recommendedInsuranceTypeId();

            if (advice?.mode === 'WAIVER' || productInsuranceTypeId == null || recommendedInsuranceTypeId == null) {
                return;
            } else if (
                productInsuranceTypeId !== recommendedInsuranceTypeId &&
                this.sameCoverageTags(this.state as OrderState, recommendedInsuranceTypeId)
            ) {
                this.setRecommendedIdAndPrice(productInsuranceTypeId);
                this.state.advice.recommendedInsuranceTypeId = productInsuranceTypeId;
            }
        });

        // set valid state for step after it was
        // already filled out once
        // Use-Case: user changes deductible or theft
        // coverage eg in step 'versichertes-geraet'
        effect(() => {
            const valid = this.valid();

            untracked(() => {
                if (this.state.advice) {
                    this.orderService.setValidState('produktberatung', !valid);
                }
            });
        });
    }

    private sameCoverageTags(state: OrderState, insuranceTypeId: number): boolean {
        const recommendedTags = this.productsService.getTagsForInsuranceTypeId(insuranceTypeId, [
            'hasDeductible',
            'hasTheftCoverage',
        ]);

        if (!recommendedTags) {
            return false;
        }

        return (
            (state.tags.get('hasDeductible') === true) === recommendedTags.hasDeductible &&
            (state.tags.get('hasTheftCoverage') === true) === recommendedTags.hasTheftCoverage
        );
    }

    private setRecommendedIdAndPrice(id: number): void {
        this.recommendedInsuranceTypeId.set(id);

        this.insuranceTypesService
            .get(id)
            .pipe(
                filter((insuranceType) => insuranceType != null),
                switchMap((insuranceType) =>
                    this.insurenetInterfaceService.getPrice(this.state as OrderState, this.product(), insuranceType),
                ),
            )
            .subscribe((price) => this.recommendedPrice.set(price));
    }

    protected evaluateRecommendation(adviceChange: { answers: ProductAdviceAnswers; complete: boolean }): void {
        this.formAnswers.set(adviceChange.answers);

        if (!adviceChange.complete) {
            this.showAdvice.set(false);
            return;
        }

        this.initialInsuranceTypeId.set(this.insuranceType().id);
        this.setRecommendedIdAndPrice(this.productsService.getInsuranceTypeIdForState(this.getRecommendedState()));

        this.showAdvice.set(true);
    }

    protected setAdviceDecisionAndSubmit(acceptSuggestion: boolean | null): void {
        const prevResultDecision = this.state?.advice?.resultDecision;
        const hasSuggestion = this.suggestTheftCoverageChange() || this.suggestDeductibleChange();

        if (hasSuggestion) {
            this.resultDecision = acceptSuggestion ? 'ACCEPTED_RECOMMENDATION' : 'KEPT_ORIGINAL';
        } else if (prevResultDecision) {
            // restore previous decision for the case when user
            // accpeted the recommendation and comes back to the
            // step and confirmes the match because the insurance
            // is already updated
            this.resultDecision = prevResultDecision;
        } else {
            this.resultDecision = 'CONFIRMED_MATCH';
        }

        if (acceptSuggestion) {
            if (this.suggestTheftCoverageChange()) {
                this.orderService.setTag('hasTheftCoverage', !this.hasTheftCoverage());
            }
            if (this.suggestDeductibleChange()) {
                this.orderService.setTag('hasDeductible', !this.hasDeductible());
            }
        } else if (this.resultDecision === 'KEPT_ORIGINAL' && prevResultDecision === 'KEPT_ORIGINAL') {
            // update insurance if user clicks not the first time
            // on 'KEPT_ORIGINAL' and necessary if the state /
            // insurance type was changed before without changing
            // the answers of the form
            this.initialInsuranceTypeId.set(this.insuranceType().id);
        }
        this.submit();
    }

    private getRecommendedState(): OrderState {
        const currentState = this.state as OrderState;

        const recommendedState: OrderState = {
            ...currentState,
            tags: new Map(currentState.tags),
        };

        recommendedState.tags.set(
            'hasTheftCoverage',
            this.suggestTheftCoverageChange() ? !this.hasTheftCoverage() : this.hasTheftCoverage(),
        );

        recommendedState.tags.set(
            'hasDeductible',
            this.suggestDeductibleChange() ? !this.hasDeductible() : this.hasDeductible(),
        );

        return recommendedState;
    }

    public override get isInvalid(): boolean {
        return !this.valid();
    }

    public getData(): Partial<OrderState> {
        const formData = this.adviceComponent().getData();

        if (formData.advice.mode === 'WAIVER') {
            return formData;
        }

        return {
            advice: {
                ...formData.advice,
                initialInsuranceTypeId: this.initialInsuranceTypeId(),
                recommendedInsuranceTypeId: this.recommendedInsuranceTypeId(),
                resultDecision: this.resultDecision,
            },
        };
    }
}
